import React from 'react';
import { 
  Form, 
  Card,
  Typography,
  Radio,
  AutoComplete,
  Button
} from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { adminSentenceApi } from '@/services/api';

const { Title } = Typography;

interface StoryDialogSectionProps {
  recordingEnabled?: boolean;
  onRecordingEnabledChange?: (enabled: boolean) => void;
}

const StoryDialogSection: React.FC<StoryDialogSectionProps> = ({
  recordingEnabled = false,
  onRecordingEnabledChange
}) => {
  const form = Form.useFormInstance();
  const [rows, setRows] = React.useState<Array<{ id: string; value: string; sid?: number }>>([]);
  const timerRef = React.useRef<number | undefined>(undefined);
  const [options, setOptions] = React.useState<{ key: string; value: string; label: string; item: any }[]>([]);

  React.useEffect(() => {
    const init = form.getFieldValue('sd_sentences') as string[] | undefined;
    const initIds = form.getFieldValue('sd_sentenceIds') as number[] | undefined;
    if (Array.isArray(init) && init.length > 0) {
      setRows(init.map((t, idx) => ({ id: String(idx + 1), value: t, sid: Array.isArray(initIds) ? initIds[idx] : undefined })));
    } else {
      setRows([{ id: '1', value: '' }]);
      form.setFieldValue('sd_sentences', []);
      form.setFieldValue('sd_sentenceIds', []);
    }
  }, [form]);

  const toKo = {
    gender: (v: string) => (v === 'male' ? '남성' : v === 'female' ? '여성' : v || ''),
    age: (v: string) => (v === 'child' ? '아동' : v === 'young' ? '청년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : v || ''),
    speed: (v: string) => (v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : v || ''),
    pitch: (v: string) => (v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : v || ''),
  } as const;

  const searchSentences = (query: string) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      try {
        const res = await adminSentenceApi.list({ q: query, page: 0, size: 200 });
        const data = res?.data || res;
        const content = data?.content || data?.data?.content || [];
        const opts = content.map((s: any) => ({
          key: String(s.id),
          value: String(s.text || ''),
          label: `${s.text} (${toKo.gender(String(s.gender||''))}|${toKo.age(String(s.age||''))}|${toKo.speed(String(s.speed||''))}|${toKo.pitch(String(s.pitch||''))})`,
          item: s,
        }));
        setOptions(opts);
      } catch (_) {
        setOptions([]);
      }
    }, 250);
  };

  const addRow = () => {
    setRows(prev => [...prev, { id: String(Date.now()), value: '' }]);
  };

  const removeRow = (rowId: string) => {
    setRows(prev => {
      const next = prev.filter(r => r.id !== rowId);
      const vals = next.map(r => r.value).filter(v => !!v);
      const ids = next.map(r => r.sid).filter((v) => v != null) as number[];
      form.setFieldValue('sd_sentences', vals);
      form.setFieldValue('sd_sentenceIds', ids);
      return next.length > 0 ? next : [{ id: '1', value: '' }];
    });
  };

  const updateRowValue = (rowId: string, value: string, sid?: number) => {
    setRows(prev => {
      const next = prev.map(r => r.id === rowId ? { ...r, value, sid: sid ?? r.sid } : r);
      const vals = next.map(r => r.value).filter(v => !!v);
      const ids = next.map(r => r.sid).filter((v) => v != null) as number[];
      form.setFieldValue('sd_sentences', vals);
      form.setFieldValue('sd_sentenceIds', ids);
      return next;
    });
  };

  // (legacy) removeSentence 제거됨 – rows 기반으로 관리됨

  return (
    <Card className='pb-5 border-b border-gray-200'>
      <Title level={4} className='mb-4'>스토리 & 다이얼로그</Title>
      
      <Form.Item
        label="녹음 여부"
        name="sd_recordingEnabled"
        rules={[
          { required: true, message: '녹음 여부를 선택해주세요' }
        ]}
      >
        <Radio.Group onChange={(e) => onRecordingEnabledChange?.(e.target.value)}>
          <Radio value={true}>녹음</Radio>
          <Radio value={false}>미녹음</Radio>
        </Radio.Group>
      </Form.Item>

      {/* 동적 오토컴플리트 행들 */}
      <div className='space-y-3'>
        {rows.map((row, index) => (
          <div key={row.id} className='flex items-center gap-3'>
            <div style={{ flex: 1 }}>
              <AutoComplete
                placeholder={`문장 선택 (${index + 1})`}
                size="large"
                value={row.value}
                onFocus={() => searchSentences('')}
                onSearch={searchSentences}
                options={options}
                filterOption={false}
                dropdownMatchSelectWidth={800}
                style={{ width: '100%', height: 48, fontSize: 16 }}
                onSelect={(_val, option: any) => {
                  const text = option?.item?.text ?? _val;
                  const sid = option?.item?.id != null ? Number(option.item.id) : undefined;
                  updateRowValue(row.id, text, sid);
                }}
                onChange={(val) => updateRowValue(row.id, val)}
              />
            </div>
            {rows.length > 1 && (
              <Button danger type='text' icon={<MinusCircleOutlined />} onClick={() => removeRow(row.id)} />
            )}
          </div>
        ))}
      </div>

      <div className='mt-3'>
        <Button type='primary' ghost onClick={addRow} size='large' block style={{ height: 48, fontSize: 16 }}>문장 추가</Button>
      </div>

      {/* hidden array field 유지 */}
      <Form.Item
        name='sd_sentences'
        hidden
        rules={[
          {
            validator: (_rule, value) => {
              const list = Array.isArray(value) ? value.filter((s) => !!String(s).trim()) : [];
              return list.length > 0
                ? Promise.resolve()
                : Promise.reject(new Error('스토리 & 다이얼로그 문장을 한 개 이상 선택해주세요'));
            }
          }
        ]}
      >
        <input />
      </Form.Item>
    </Card>
  );
};

export default StoryDialogSection; 